/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { AccountPageLayout } from "components/common/AccountPageLayout";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { UserLabel } from "components/common/userLabel/UserLabel";
import { Fetching } from "components/common/loading/fetching";
import { SafeDateTimeRangePicker } from "components/SafeDateTimeRangePicker";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { getExceptionalStatusTranslationKey } from "src/utils/getExceptionalStatusTranslationKey";
import { useTenant } from "hooks/settings";
import { userDisplayName } from "src/utils/userDisplay";
import { useCallback, useMemo, useState } from "react";
import { ExceptionVideoPreview } from "./ExceptionVideoPreview";
import {
  LocationExceptionalStatusRead,
  LocationExceptionalStatusType,
  useCreateLocationExceptionalStatusMutation,
  useDeleteLocationExceptionalStatusMutation,
  useGetScrapEyeMetadataQuery,
  useListLocationExceptionalStatusesQuery,
  useUpdateLocationExceptionalStatusMutation,
} from "src/store/api/generatedApi";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

dayjs.extend(utc);

type StatusRow = LocationExceptionalStatusRead & { rowId: number };

type TargetMode = "location" | "area";

const LOCATION_STATUS_OPTIONS = [
  "ignorable",
  "missing",
  "bad_labels",
] as const satisfies readonly LocationExceptionalStatusType[];
const AREA_STATUS_OPTIONS = [
  "broken_scale_weights",
] as const satisfies readonly LocationExceptionalStatusType[];

export const LocationExceptionalStatusPage = () => {
  const { t } = useTenantTranslation();
  const tenantName = useTenant();
  const [selectedLocationName, setSelectedLocationName] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<StatusRow | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const { locationNames } = useLabellingLocations();

  const { data: scrapEyeMetadata } = useGetScrapEyeMetadataQuery({
    tenantName,
  });
  const scaleWeightAreas = useMemo(
    () => scrapEyeMetadata?.scale_weight_areas ?? [],
    [scrapEyeMetadata]
  );
  const tenantSupportsAreas = scaleWeightAreas.length > 0;

  const {
    data: exceptionalStatusData,
    isLoading,
    isFetching,
  } = useListLocationExceptionalStatusesQuery({
    tenantName,
    locationName: selectedLocationName || undefined,
    area: selectedArea || undefined,
  });

  const rows: StatusRow[] = useMemo(
    () =>
      (exceptionalStatusData?.items ?? []).map((item) => ({
        ...item,
        rowId: item.id,
      })),
    [exceptionalStatusData]
  );

  const [deleteStatus] = useDeleteLocationExceptionalStatusMutation();

  const handleConfirmDelete = useCallback(() => {
    if (pendingDeleteId === null) return;
    void deleteStatus({ tenantName, statusId: pendingDeleteId });
    setPendingDeleteId(null);
  }, [deleteStatus, tenantName, pendingDeleteId]);

  const handleEdit = useCallback((row: StatusRow) => {
    setEditingRow(row);
    setDialogOpen(true);
  }, []);

  const handleAdd = useCallback(() => {
    setEditingRow(null);
    setDialogOpen(true);
  }, []);

  const columns = useMemo(
    (): GridColDef<StatusRow>[] => [
      {
        field: "location_name",
        headerName: t("location"),
        width: 160,
      },
      {
        field: "area",
        headerName: t("area"),
        width: 140,
      },
      {
        field: "status",
        headerName: t("status"),
        width: 160,
        valueFormatter: (value: string) =>
          t(
            getExceptionalStatusTranslationKey(
              value as LocationExceptionalStatusType
            )
          ),
      },
      {
        field: "started_at",
        headerName: t("startedAt"),
        width: 200,
        valueFormatter: (value: string) =>
          dayjs.utc(value).format("YYYY-MM-DD HH:mm"),
      },
      {
        field: "ended_at",
        headerName: t("endedAt"),
        width: 200,
        valueFormatter: (value: string) =>
          dayjs.utc(value).format("YYYY-MM-DD HH:mm"),
      },
      {
        field: "note",
        headerName: t("note"),
        flex: 1,
        minWidth: 200,
      },
      {
        field: "created_by",
        headerName: t("createdBy"),
        width: 160,
        valueGetter: (_value, row) => userDisplayName(row.created_by),
        renderCell: (params) => <UserLabel user={params.row.created_by} />,
      },
      {
        field: "actions",
        headerName: "",
        width: 100,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row">
            <IconButton
              size="small"
              onClick={() => {
                handleEdit(params.row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setPendingDeleteId(params.row.id);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
      },
    ],
    [t, handleEdit]
  );

  return (
    <AccountPageLayout
      title={t("exceptions")}
      actionButton={
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
          {t("addExceptionalPeriod")}
        </Button>
      }
    >
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <FormControl sx={{ width: 240 }}>
            <InputLabel>{t("location")}</InputLabel>
            <Select
              value={selectedLocationName}
              label={t("location")}
              onChange={(event) => {
                setSelectedLocationName(event.target.value);
              }}
            >
              <MenuItem value="">{t("all")}</MenuItem>
              {locationNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {tenantSupportsAreas && (
            <FormControl sx={{ width: 240 }}>
              <InputLabel>{t("area")}</InputLabel>
              <Select
                value={selectedArea}
                label={t("area")}
                onChange={(event) => {
                  setSelectedArea(event.target.value);
                }}
              >
                <MenuItem value="">{t("all")}</MenuItem>
                {scaleWeightAreas.map((area) => (
                  <MenuItem key={area} value={area}>
                    {area}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        <Fetching fetching={isLoading}>
          <HotkeyCompliantDataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.rowId}
            loading={isFetching}
            autoHeight
            disableRowSelectionOnClick
          />
        </Fetching>
      </Stack>

      <ExceptionalStatusDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
        editingRow={editingRow}
        locationNames={locationNames}
        scaleWeightAreas={scaleWeightAreas}
        defaultLocation={selectedLocationName}
        defaultArea={selectedArea}
        tenantName={tenantName}
      />

      <Dialog
        open={pendingDeleteId !== null}
        onClose={() => {
          setPendingDeleteId(null);
        }}
      >
        <DialogTitle>{t("deleteExceptionalPeriod")}</DialogTitle>
        <DialogContent>{t("confirmDelete")}</DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setPendingDeleteId(null);
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
          >
            {t("delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </AccountPageLayout>
  );
};

const ExceptionalStatusDialog = ({
  open,
  onClose,
  editingRow,
  locationNames,
  scaleWeightAreas,
  defaultLocation,
  defaultArea,
  tenantName,
}: {
  open: boolean;
  onClose: () => void;
  editingRow: StatusRow | null;
  locationNames: readonly string[];
  scaleWeightAreas: string[];
  defaultLocation: string;
  defaultArea: string;
  tenantName: string;
}) => {
  const { t } = useTenantTranslation();
  const [createStatus] = useCreateLocationExceptionalStatusMutation();
  const [updateStatus] = useUpdateLocationExceptionalStatusMutation();

  const tenantSupportsAreas = scaleWeightAreas.length > 0;

  const [targetMode, setTargetMode] = useState<TargetMode>("location");
  const [locationName, setLocationName] = useState("");
  const [area, setArea] = useState("");
  const [status, setStatus] =
    useState<LocationExceptionalStatusType>("ignorable");
  const [startedAt, setStartedAt] = useState<Dayjs | null>(null);
  const [endedAt, setEndedAt] = useState<Dayjs | null>(null);
  const [note, setNote] = useState("");
  const resetForm = useCallback(() => {
    if (editingRow) {
      const editingMode: TargetMode =
        editingRow.area !== null ? "area" : "location";
      setTargetMode(editingMode);
      setLocationName(editingRow.location_name ?? "");
      setArea(editingRow.area ?? "");
      setStatus(editingRow.status);
      setStartedAt(dayjs.utc(editingRow.started_at));
      setEndedAt(dayjs.utc(editingRow.ended_at));
      setNote(editingRow.note ?? "");
    } else {
      setTargetMode("location");
      setLocationName(defaultLocation);
      setArea(defaultArea);
      setStatus("ignorable");
      setStartedAt(null);
      setEndedAt(null);
      setNote("");
    }
  }, [editingRow, defaultLocation, defaultArea]);

  const handleTargetModeChange = useCallback(
    (_event: unknown, value: TargetMode | null) => {
      if (value === null) return;
      setTargetMode(value);
      setStatus(value === "area" ? "broken_scale_weights" : "ignorable");
    },
    []
  );

  const handleDateRangeChange = useCallback(
    (value: [Dayjs | null, Dayjs | null]) => {
      setStartedAt(value[0]);
      setEndedAt(value[1]);
    },
    []
  );

  const targetSelected =
    targetMode === "location" ? locationName !== "" : area !== "";
  const canSubmit = targetSelected && startedAt !== null && endedAt !== null;
  const canPreviewVideo = targetMode === "location" && locationName !== "";

  const statusOptions: readonly LocationExceptionalStatusType[] =
    targetMode === "area" ? AREA_STATUS_OPTIONS : LOCATION_STATUS_OPTIONS;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || startedAt === null || endedAt === null) return;

    const body = {
      started_at: startedAt.utc().toISOString(),
      ended_at: endedAt.utc().toISOString(),
      status,
      note: note || null,
    };

    try {
      if (editingRow) {
        await updateStatus({
          tenantName,
          statusId: editingRow.id,
          locationExceptionalStatusUpdate: body,
        }).unwrap();
      } else {
        const createBody =
          targetMode === "area"
            ? { ...body, area }
            : { ...body, location_name: locationName };
        await createStatus({
          tenantName,
          locationExceptionalStatusCreate: createBody,
        }).unwrap();
      }
      onClose();
    } catch {
      // Swallow the unwrap rejection so the dialog stays open instead of
      // closing onto a half-applied state — the user can retry or cancel.
    }
  }, [
    canSubmit,
    startedAt,
    endedAt,
    status,
    note,
    editingRow,
    tenantName,
    targetMode,
    area,
    locationName,
    createStatus,
    updateStatus,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{ transition: { onEntered: resetForm } }}
    >
      <DialogTitle>
        {editingRow ? t("editExceptionalPeriod") : t("addExceptionalPeriod")}
      </DialogTitle>
      <DialogContent>
        <Stack direction="row" spacing={3} sx={{ mt: 1 }}>
          <Stack spacing={2} sx={{ width: 400, flexShrink: 0 }}>
            {tenantSupportsAreas && (
              <ToggleButtonGroup
                value={targetMode}
                exclusive
                onChange={handleTargetModeChange}
                disabled={editingRow !== null}
                size="small"
              >
                <ToggleButton value="location">{t("location")}</ToggleButton>
                <ToggleButton value="area">{t("area")}</ToggleButton>
              </ToggleButtonGroup>
            )}

            {targetMode === "location" ? (
              <FormControl fullWidth disabled={editingRow !== null}>
                <InputLabel>{t("location")}</InputLabel>
                <Select
                  value={locationName}
                  label={t("location")}
                  onChange={(event) => {
                    setLocationName(event.target.value);
                  }}
                >
                  {locationNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <FormControl fullWidth disabled={editingRow !== null}>
                <InputLabel>{t("area")}</InputLabel>
                <Select
                  value={area}
                  label={t("area")}
                  onChange={(event) => {
                    setArea(event.target.value);
                  }}
                >
                  {scaleWeightAreas.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <RadioGroup
              row
              value={status}
              onChange={(event) => {
                setStatus(event.target.value as LocationExceptionalStatusType);
              }}
            >
              {statusOptions.map((option) => (
                <FormControlLabel
                  key={option}
                  value={option}
                  control={<Radio />}
                  label={t(getExceptionalStatusTranslationKey(option))}
                />
              ))}
            </RadioGroup>

            <SafeDateTimeRangePicker
              value={[startedAt, endedAt]}
              onChange={handleDateRangeChange}
              ampm={false}
              format="MM-DD-YYYY HH:mm"
              timezone="UTC"
            />

            <TextField
              label={t("note")}
              value={note}
              onChange={(event) => {
                setNote(event.target.value);
              }}
              multiline
              rows={2}
            />
          </Stack>

          {canPreviewVideo ? (
            <ExceptionVideoPreview
              location={locationName}
              startedAt={startedAt}
              endedAt={endedAt}
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          variant="contained"
          onClick={() => void handleSubmit()}
          disabled={!canSubmit}
        >
          {editingRow ? t("save") : t("create")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
